import React from 'react'

import cx from 'classnames'

import { CircleLoader, Div, H5, H6 } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { DeserializedSavedFilter } from 'api'
import SavedFiltersActionMenu from 'components/SavedFiltersActionMenu'
import { isEU } from 'config'
import { INACTIVE_FILTER } from 'hooks/useSavedFilters'

import actionStyles from '../SavedFiltersActionMenu/styles.module.css'
import styles from './styles.module.css'

interface SavedViewsSidebarProps {
  savedFilters?: DeserializedSavedFilter[]
  activeFilterId?: number
  defaultFilterId?: number
  setActiveFilterId: (id: number | undefined) => void
  updateDefaultFilterId: (id: number) => void
  removeSavedFilter: (id: number) => void
  onOpenEditModal: () => void
  onCreateSavedFilter: () => void
  onShowFeedbackModal: () => void
  isExternalPage: boolean
}

const SavedViewsSidebar = ({
  savedFilters,
  activeFilterId,
  defaultFilterId,
  setActiveFilterId,
  updateDefaultFilterId,
  removeSavedFilter,
  onOpenEditModal,
  onCreateSavedFilter,
  onShowFeedbackModal,
  isExternalPage
}: SavedViewsSidebarProps) => {
  const highlightAllAccounts = activeFilterId === INACTIVE_FILTER

  if (savedFilters === undefined) {
    return (
      <Div className={styles.SavedFiltersLoading}>
        <CircleLoader />
      </Div>
    )
  }

  const privateFilters = savedFilters
    ?.filter(filter => !filter.shared)
    .sort((a, b) => a.name.localeCompare(b.name))
  const sharedFilters = savedFilters
    ?.filter(filter => filter.shared)
    .sort((a, b) => a.name.localeCompare(b.name))

  const showToolTip = ({ name }: DeserializedSavedFilter): boolean => name.length > 19

  return (
    <Div className={styles.ActivitiesSidebar}>
      <Div className={styles.SidebarTopSection}>
        <H6 className={styles.ActivitiesViewItemAccounts}>Accounts</H6>
        <Div
          onClick={() => setActiveFilterId(undefined)}
          className={cx(actionStyles.ActivitiesViewItem, {
            CurrentViewItem: highlightAllAccounts,
            [actionStyles.CurrentViewItem]: highlightAllAccounts
          })}>
          <H5>All accounts</H5>
        </Div>
        {!isEU && !isExternalPage && (
          <a target="_blank" href="/live">
            <Div
              className={cx([
                actionStyles.ActivitiesViewItem,
                actionStyles.ActivitiesLiveViewItem
              ])}>
              <H5>Live view</H5>
              <Icon height={16} width={16} name="open-new-tab" />
            </Div>
          </a>
        )}
        {(!privateFilters || privateFilters.length > 0) && (
          <React.Fragment>
            <H6 className={styles.ActivitySectionSavedFilters}>Your Saved Filters</H6>
            {privateFilters.map((filter: DeserializedSavedFilter) => (
              <SavedFiltersActionMenu
                key={filter.id}
                savedFilter={filter}
                isActive={activeFilterId === filter.id}
                defaultFilterId={defaultFilterId}
                onOpenEditModal={onOpenEditModal}
                updateDefaultFilterId={updateDefaultFilterId}
                removeSavedFilter={removeSavedFilter}
                setActiveFilterId={setActiveFilterId}
                showToolTip={showToolTip(filter)}
              />
            ))}
          </React.Fragment>
        )}
        {(!sharedFilters || sharedFilters.length > 0) && (
          <React.Fragment>
            <H6 className={styles.ActivitySectionSavedFilters}>Shared Saved Filters</H6>
            {sharedFilters.map((filter: DeserializedSavedFilter) => (
              <SavedFiltersActionMenu
                key={filter.id}
                savedFilter={filter}
                isActive={activeFilterId === filter.id}
                defaultFilterId={defaultFilterId}
                onOpenEditModal={onOpenEditModal}
                updateDefaultFilterId={updateDefaultFilterId}
                removeSavedFilter={removeSavedFilter}
                setActiveFilterId={setActiveFilterId}
                showToolTip={showToolTip(filter)}
              />
            ))}
          </React.Fragment>
        )}

        <hr className={styles.Separator} />
        <Div className={actionStyles.ActivitiesViewItem} onClick={onCreateSavedFilter}>
          <H5>Create saved filter</H5>
          <Icon name="plus" height={12} width={12} />
        </Div>
      </Div>

      <Div className={styles.SidebarBottomSection}>
        <Div onClick={onShowFeedbackModal} className={styles.FeedbackButton}>
          <Icon className={styles.FeedbackIcon} name="megaphone-2" height={14} width={16} />
          <Div className={styles.FeedbackPrompt}>Give product feedback</Div>
        </Div>
      </Div>
    </Div>
  )
}

export default SavedViewsSidebar
